/**
 * Bucket rules mirroring the backend (contract §1.3, crates/flow-query
 * bucket.rs): from the allowed set {60, 300, 900, 3600, 14400, 86400} pick
 * the finest bucket that yields at most MAX_BUCKETS (2000) buckets over the
 * range. Candles additionally require ≥ 3 trades in the median non-empty
 * bucket — that part needs the data and stays server-side; the server's
 * choice always arrives in `meta.bucket_secs` (the API client publishes it
 * to `useUi.serverBucket`, which the BucketPicker displays).
 *
 * The frontend sends no bucket for "auto"; `autoBucket` is only the local
 * prediction used before a response arrives (range snapping, labels).
 * `defaultBucket` is the readability-oriented choice for client-side
 * bucketing (≤ DEFAULT_MAX_BUCKETS): 24h → 5m instead of 1m.
 */
import { BUCKETS, type Bucket } from "@/api/types";

/** Server cap on auto-sized series (flow-query `MAX_BUCKETS`). */
export const MAX_BUCKETS = 2000;

/** Readability cap for client-side bucketing: 1h/6h → 1m, 24h → 5m, 7d → 15m, 30d → 1h. */
export const DEFAULT_MAX_BUCKETS = 720;

/** Finest allowed bucket giving at most `max` buckets (ceil, like the server's `div_ceil`). */
export function autoBucket(fromSecs: number, toSecs: number, max = MAX_BUCKETS): Bucket {
  const span = Math.max(1, toSecs - fromSecs);
  for (const b of BUCKETS) {
    if (Math.ceil(span / b) <= max) return b;
  }
  return BUCKETS[BUCKETS.length - 1]!;
}

/** Readable default for charts the client buckets itself (24h → 5m rather than 1m). */
export function defaultBucket(fromSecs: number, toSecs: number): Bucket {
  return autoBucket(fromSecs, toSecs, DEFAULT_MAX_BUCKETS);
}

export function isBucket(v: number): v is Bucket {
  return (BUCKETS as readonly number[]).includes(v);
}

const LABELS: Record<Bucket, string> = {
  60: "1m",
  300: "5m",
  900: "15m",
  3600: "1h",
  14400: "4h",
  86400: "1d",
};

export function bucketLabel(secs: number | undefined): string {
  if (secs === undefined) return "auto";
  if (isBucket(secs)) return LABELS[secs];
  if (secs % 86400 === 0) return `${secs / 86400}d`;
  if (secs % 3600 === 0) return `${secs / 3600}h`;
  if (secs % 60 === 0) return `${secs / 60}m`;
  return `${secs}s`;
}

/** Bucket start for a timestamp (half-open [t, t+bucket)). */
export function bucketStart(t: number, bucket: number): number {
  return Math.floor(t / bucket) * bucket;
}

/** Number of buckets a range would produce at a given size. */
export function bucketCount(fromSecs: number, toSecs: number, bucket: number): number {
  return Math.ceil(Math.max(0, toSecs - fromSecs) / bucket);
}
